/** @jsxImportSource @emotion/react */
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { Star, Verified } from '../../Icons';
import { H3 } from '../../Typography';
import { Button, ResizablePanel, Slidernation, Slideshow } from '../../UI';
import { reviews as defaultReviews } from './defaultReviews';
import useStyle from './style';

export type ReviewType = {
  reviewerName: string;
  reviewDate?: string;
  reviewContent: string;
  profileImage: { url: string; alt: string };
  customData: string;
  id: string;
};

const CustomersLoveCove: React.FC<{
  reviews: ReviewType[];
  ctaLink?: string;
}> = ({ reviews = defaultReviews, ctaLink = '/quiz' }) => {
  const {
    componentWrapper,
    componentHeader,
    buttonsWrapper,
    componentButton,
    reviewsContainer,
    reviewsContainerChildren,
    mobileSlideShowWrapper,
    mobileSlideShowStyle,
    slideShowWrapper,
    ctaWrapper,
    seeMoreReviewsStyle,
  } = useStyle();

  const [currentCategory, setCurrentCategory] = useState(0);
  const [currSlide, setCurrSlide] = useState(0);

  const formattedReviews = reviews.map(review => ({
    ...review,
    customData: JSON.parse(review.customData),
  }));

  const getCategories = () => {
    const categories = formattedReviews.map(
      review => review.customData.categories
    );
    const finalCategories: string[] = [];

    for (let i = 0; i < categories.length; i += 1) {
      categories[i].forEach((category: string) => {
        if (!finalCategories.includes(category)) {
          finalCategories.push(category);
        }
      });
    }
    return finalCategories;
  };

  const categories = getCategories();

  const filteredReviews = formattedReviews.filter(review =>
    review.customData.categories.includes(categories[currentCategory])
  );

  return (
    <div css={componentWrapper}>
      <H3 css={componentHeader}>Why do our customers love Cove?</H3>
      <p>See why our customers rate us #1</p>
      <div css={buttonsWrapper}>
        {categories.map((category, categoryIndex) => (
          <Button
            cypressId={`customers-love-cove-category-${categoryIndex}`}
            style={currentCategory === categoryIndex ? 'container' : 'outlined'}
            size="large"
            color={currentCategory === categoryIndex ? 'darkblue' : 'purple'}
            onClick={() => setCurrentCategory(categoryIndex)}
            css={componentButton}
            key={`category-${categoryIndex}`}
          >
            {category.replaceAll('-', ' ')}
          </Button>
        ))}
      </div>
      <ResizablePanel
        childrenCss={reviewsContainerChildren}
        css={reviewsContainer}
      >
        <AnimatePresence mode="popLayout">
          {filteredReviews.map(review => {
            return (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={review.id}
                style={{ width: '100%' }}
              >
                <ReviewCard review={review} />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </ResizablePanel>
      <ResizablePanel
        childrenCss={mobileSlideShowStyle}
        css={mobileSlideShowWrapper}
      >
        <Slideshow
          slideshowWrapper={slideShowWrapper}
          runOnChange={slide => setCurrSlide(slide)}
          currSlide={currSlide}
          slides={filteredReviews.map(review => {
            return (
              <div key={review.id} style={{ width: '100%' }}>
                <ReviewCard review={review} />
              </div>
            );
          })}
        />
        <Slidernation
          bordered
          darkTabs
          rounded
          slideCount={filteredReviews.length}
          activeSlide={currSlide}
          setSlide={slide => setCurrSlide(slide)}
        />
      </ResizablePanel>
      <Button
        cypressId="customers-love-cove-read-reviews"
        href="/reviews/"
        color="purple"
        isUnderlined
        style="text"
        css={seeMoreReviewsStyle}
      >
        Read more reviews
      </Button>
      <div css={ctaWrapper}>
        <Button
          cypressId="customers-love-cove-cta"
          href={ctaLink}
          color="purple"
          size="large"
          style="container"
        >
          Choose My Equipment
        </Button>
        <p>Takes less than 1 minute</p>
      </div>
    </div>
  );
};

export default CustomersLoveCove;

const ReviewCard: React.FC<{ review: ReviewType }> = ({ review, ...props }) => {
  const { profileImage, reviewContent, reviewDate, reviewerName } = review;
  const { reviewCard, reviewCardPerson, starsWrapper } = useStyle();

  return (
    <div css={reviewCard} {...props}>
      <div css={reviewCardPerson}>
        <img
          src={profileImage.url}
          alt={profileImage.alt}
          width={60}
          height={60}
        />
        <div>
          <b>{reviewerName}</b>
          {reviewDate && (
            <i>
              {reviewDate} <Verified />
            </i>
          )}
        </div>
      </div>
      <div css={starsWrapper}>
        <Star />
        <Star />
        <Star />
        <Star />
        <Star />
      </div>
      <p>{reviewContent}</p>
    </div>
  );
};
